import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronLeft, X } from 'lucide-react';
import SEO, { getServiceSchema } from '../components/SEO';

// Portfolio drone images for lightbox
const portfolioDroneImages = [
  {
    id: 'drone-1',
    src: 'https://customer-assets.emergentagent.com/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/53m0md4t_046.jpg',
    alt: 'Aerial view of residential property with mountain backdrop'
  },
  {
    id: 'drone-2',
    src: 'https://customer-assets.emergentagent.com/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/2184pue1_05-Drone.jpg',
    alt: 'Top-down aerial view of property with tile roof'
  }
];

const AerialPhotosVideo = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const serviceSchema = getServiceSchema({
    name: 'Aerial Drone Photography & Video',
    description: 'Professional drone photography and video services for real estate, events, and commercial projects in Arizona. Fully insured.',
    url: 'https://rcstudios.net/services/aerial-photos-video',
    image: 'https://customer-assets.emergentagent.com/job_premium-media-8/artifacts/eln1jjff_Hero-aerial-drone.jpg',
    areaServed: 'Arizona',
    priceRange: '$149-$349'
  });

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!lightboxOpen) return;
    
    if (e.key === 'Escape') {
      setLightboxOpen(false);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setLightboxIndex((prev) => (prev + 1) % portfolioDroneImages.length);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setLightboxIndex((prev) => (prev - 1 + portfolioDroneImages.length) % portfolioDroneImages.length);
    }
  }, [lightboxOpen]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxOpen, handleKeyDown]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goNext = () => {
    setLightboxIndex((prev) => (prev + 1) % portfolioDroneImages.length);
  };

  const goPrev = () => {
    setLightboxIndex((prev) => (prev - 1 + portfolioDroneImages.length) % portfolioDroneImages.length);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Aerial Drone Photography & Video"
        description="Professional drone photography and video services in Kingman, Lake Havasu City, and Mohave County. Stunning aerial perspectives for real estate and commercial projects. Fully insured."
        path="/services/aerial-photos-video"
        image="https://customer-assets.emergentagent.com/job_premium-media-8/artifacts/eln1jjff_Hero-aerial-drone.jpg"
        jsonLd={serviceSchema}
      />
      {/* Hero Section - Clean Image Only */}
      <section className="hero-main service-hero-aerial relative h-[400px] md:h-[480px] lg:h-[520px] overflow-hidden">
        {/* Subtle cinematic vignette for navbar contrast only */}
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#050816]/60 to-transparent z-10"></div>
      </section>

      {/* Breadcrumb Section */}
      <section className="bg-[#FAF9F6] border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-5 md:py-6">
          <div className="flex items-center gap-3">
            <Link 
              to="/services" 
              className="text-[11px] font-medium tracking-[0.2em] uppercase text-[#F5C056] hover:text-[#F5C056]/80 transition-colors"
            >
              Services
            </Link>
            <span className="text-[#050816]/30 text-xs">/</span>
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#050816]">
              Aerial Photos & Video
            </span>
          </div>
          <div className="w-12 h-[1.5px] bg-[#F5C056]/40 mt-4"></div>
        </div>
      </section>

      {/* SECTION 1 — Aerial Photography Portfolio Gallery */}
      <section className="py-16 md:py-20 bg-[#050816]">
        <div className="max-w-[1200px] mx-auto px-4">
          {/* Section Header */}
          <div className="mb-10">
            <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase mb-3">Portfolio</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Aerial Work
            </h2>
            <p className="text-gray-400 max-w-[500px]">
              A curated sample of recent aerial imagery for listings, land, and neighborhoods.
            </p>
            {/* Gold Divider */}
            <div className="w-16 h-[2px] bg-[#F5C056]/60 mt-6"></div>
          </div>

          {/* Gallery Grid - 8 image cards */}
          {/* Individual aerial images - can be swapped with actual portfolio shots */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              { id: 1, src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/qx4zj8ru_ChatGPT%20Image%20Feb%208%2C%202026%2C%2005_31_53%20PM.png", alt: "Luxury neighborhood sunset aerial" },
              { id: 2, src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/uj5p5las_istockphoto-1066713658-612x612.jpg", alt: "Top-down neighborhood cul-de-sac aerial" },
              { id: 3, src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/rovsnxq1_ChatGPT%20Image%20Feb%208%2C%202026%2C%2005_39_10%20PM.png", alt: "Aerial dam and reservoir desert landscape" },
              { id: 4, src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/io9xze9c_ChatGPT%20Image%20Feb%208%2C%202026%2C%2005_46_46%20PM.png", alt: "Mountain cabin autumn foliage aerial" },
              { id: 5, src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/wadk7u86_ChatGPT%20Image%20Feb%208%2C%202026%2C%2005_51_23%20PM.png", alt: "Twilight modern homes aerial" },
              { id: 6, src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/6e4n9ikx_gettyimages-1400308569-640x640.jpg", alt: "Downtown cityscape twilight aerial" },
              { id: 7, src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/50d92xg5_gettyimages-2184602195-640x640.jpg", alt: "City skyline sunset aerial" },
              { id: 8, src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/u6ca9gy7_ChatGPT%20Image%20Feb%208%2C%202026%2C%2006_13_56%20PM.png", alt: "Red rock canyon landscape aerial" },
            ].map((image) => (
              <div 
                key={image.id}
                className="group relative overflow-hidden rounded-xl border border-white/10 shadow-lg bg-[#0a0f1a]"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover object-center transition-all duration-300 group-hover:scale-[1.03] group-hover:brightness-110"
                  />
                </div>
                {/* Hover glow effect */}
                <div className="absolute inset-0 rounded-xl border-2 border-[#F5C056]/0 group-hover:border-[#F5C056]/20 transition-all duration-300 pointer-events-none"></div>
              </div>
            ))}
          </div>

          {/* Highlights */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 rounded-full bg-[#F5C056] mt-2 flex-shrink-0"></div>
              <p className="text-gray-300">Fully insured with Progressive liability insurance coverage</p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 rounded-full bg-[#F5C056] mt-2 flex-shrink-0"></div>
              <p className="text-gray-300">4K resolution imagery optimized for MLS and print</p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 rounded-full bg-[#F5C056] mt-2 flex-shrink-0"></div>
              <p className="text-gray-300">24–48 hour turnaround on standard aerial packages</p>
            </div>
          </div>

          {/* View Full Gallery Link */}
          <div className="text-left">
            <Link 
              to="/pricing#aerial" 
              className="inline-flex items-center text-[#F5C056] hover:text-[#F5C056]/80 hover:underline transition-all text-sm tracking-wide"
            >
              View Full Gallery
              <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </div>

          {/* Additional Portfolio Drone Photos */}
          <div className="mt-12 pt-10 border-t border-white/10">
            <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase mb-4">Recent Work</p>
            <h3 className="font-serif text-2xl text-white mb-6">Real Estate Drone Photography</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {portfolioDroneImages.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => openLightbox(index)}
                  className="group relative overflow-hidden rounded-xl border border-white/10 shadow-lg bg-[#0a0f1a] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#F5C056]/50"
                  data-testid={`drone-gallery-${index}`}
                >
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover object-center transition-all duration-300 group-hover:scale-[1.03] group-hover:brightness-110"
                    />
                  </div>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#050816]/80 px-4 py-2 rounded-full">
                      View Full Size
                    </span>
                  </div>
                  {/* Hover glow effect */}
                  <div className="absolute inset-0 rounded-xl border-2 border-[#F5C056]/0 group-hover:border-[#F5C056]/30 transition-all duration-300 pointer-events-none"></div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Drop Pins / Neighborhood Highlights */}
      <section className="pt-20 pb-12 bg-[#FAF9F6]">
        <div className="max-w-[1200px] mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left - Text Content */}
            <div className="order-2 md:order-1">
              <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase mb-4">Add-On</p>
              <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
                Drop Pins & Neighborhood Context
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Help buyers understand the full picture. Custom drop pins highlight nearby schools, parks, shopping centers, and other points of interest—giving context that static photos can't provide.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Perfect for marketing luxury homes in desirable neighborhoods, showcasing proximity to amenities, or highlighting waterfront and golf course locations.
              </p>

              {/* Feature Chips */}
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-[#050816] text-white text-sm font-medium rounded-full">
                  Custom Pins
                </span>
                <span className="px-4 py-2 bg-[#050816] text-white text-sm font-medium rounded-full">
                  Branded Labels
                </span>
                <span className="px-4 py-2 bg-[#050816] text-white text-sm font-medium rounded-full">
                  MLS Friendly
                </span>
              </div>
            </div>

            {/* Right - Single Image Card */}
            <div className="order-1 md:order-2">
              {/* Card A - Drop Pins Aerial */}
              {/* IMAGE A: Replace this src independently */}
              <div className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all duration-250">
                <div className="relative aspect-[16/10] lg:h-[360px]">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/koljo1po_Pin-property.jpg"
                    alt="Aerial view with drop pins"
                    className="w-full h-full object-cover object-center transition-transform duration-250 group-hover:scale-[1.02]"
                  />
                  {/* Subtle gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-[rgba(10,14,24,0.15)] to-transparent pointer-events-none"></div>
                  {/* Label chip */}
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 bg-[rgba(5,8,22,0.75)] backdrop-blur-sm text-[#F5C056] text-[9px] font-medium uppercase tracking-[0.1em] rounded">
                      MLS-Friendly Pins
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — Property Outlines */}
      <section className="pt-12 pb-12 bg-white">
        <div className="max-w-[1200px] mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left - Image */}
            <div>
              <div className="overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/5wag2vd3_ChatGPT%20Image%20Feb%208%2C%202026%2C%2006_52_29%20PM.png"
                  alt="Property outline styles"
                  className="w-full h-[360px] md:h-[420px] object-cover object-top"
                />
              </div>
            </div>

            {/* Right - Content */}
            <div>
              <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase mb-4">Add-On</p>
              <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
                Property Outlines
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed mb-6">
                Clearly define property boundaries and lot lines with professional overlay options. Choose from three distinct styles to match your listing's aesthetic.
              </p>

              {/* Feature Cards */}
              <div className="space-y-3">
                <div className="group flex items-start gap-4 p-4 bg-gradient-to-r from-[#FAF9F6] to-white rounded-xl border border-gray-100 hover:border-[#F5C056]/30 hover:shadow-sm transition-all">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#050816] flex items-center justify-center">
                    <span className="text-[#F5C056] text-xs font-bold">01</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm mb-1">Classic Property Outline</h3>
                    <p className="text-muted-foreground text-xs leading-relaxed">Clean white boundary lines that clearly define the property perimeter without distraction.</p>
                  </div>
                </div>
                <div className="group flex items-start gap-4 p-4 bg-gradient-to-r from-[#FAF9F6] to-white rounded-xl border border-gray-100 hover:border-[#F5C056]/30 hover:shadow-sm transition-all">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#050816] flex items-center justify-center">
                    <span className="text-[#F5C056] text-xs font-bold">02</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm mb-1">Artistic Property Outline</h3>
                    <p className="text-muted-foreground text-xs leading-relaxed">Stylized overlay with address callout—perfect for social media and marketing materials.</p>
                  </div>
                </div>
                <div className="group flex items-start gap-4 p-4 bg-gradient-to-r from-[#FAF9F6] to-white rounded-xl border border-gray-100 hover:border-[#F5C056]/30 hover:shadow-sm transition-all">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#050816] flex items-center justify-center">
                    <span className="text-[#F5C056] text-xs font-bold">03</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm mb-1">Aerial Color Fade</h3>
                    <p className="text-muted-foreground text-xs leading-relaxed">Grayscale surrounding with color-highlighted property—makes your listing pop instantly.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — Sunset Aerial Photography */}
      <section className="pt-12 pb-20 bg-[#050816]">
        <div className="max-w-[1200px] mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left - Image */}
            <div>
              <div className="overflow-hidden rounded-2xl shadow-2xl border border-white/10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/h6kj2l7c_ChatGPT%20Image%20Feb%208%2C%202026%2C%2006_58_16%20PM.png"
                  alt="Sunset aerial photography examples"
                  className="w-full h-[360px] md:h-[420px] object-cover object-center"
                />
              </div>
            </div>

            {/* Right - Content */}
            <div>
              <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase mb-4">Premium Add-On</p>
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
                Sunset & Twilight Aerials
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Capture the magic hour. Twilight and sunset aerial photography transforms ordinary listings into extraordinary marketing assets—perfect for luxury properties that deserve a dramatic first impression.
              </p>
              <p className="text-gray-400 leading-relaxed mb-8">
                Scheduled during golden hour for optimal lighting. Fast turnaround with same-week delivery available for most shoots.
              </p>

              {/* CTA Button */}
              <Button 
                asChild 
                className="bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-medium transition-all shadow-lg hover:shadow-xl"
              >
                <Link to="/contact">
                  Request Aerials
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="lightbox-overlay"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm animate-in fade-in duration-200" />
          
          {/* Lightbox Container */}
          <div 
            className="relative w-full max-w-5xl max-h-[90vh] animate-in zoom-in-95 fade-in duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-all z-10"
              aria-label="Close lightbox"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Image Container */}
            <div className="relative bg-[#0a0f1a] rounded-2xl overflow-hidden shadow-2xl border border-white/10">
              <img
                src={portfolioDroneImages[lightboxIndex].src}
                alt={portfolioDroneImages[lightboxIndex].alt}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              
              {/* Navigation Arrows */}
              <button
                onClick={goPrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white/70 hover:text-white transition-all"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <button
                onClick={goNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white/70 hover:text-white transition-all"
                aria-label="Next image"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* Image Counter & Caption */}
            <div className="mt-4 flex items-center justify-between text-white/70 px-2">
              <p className="text-sm">{portfolioDroneImages[lightboxIndex].alt}</p>
              <p className="text-sm">
                {lightboxIndex + 1} / {portfolioDroneImages.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AerialPhotosVideo;
